import { fetchSessionCustomerId } from '@/api/customer/sessionCustomer';
import { storage, storageKeys } from '@/constants/storage';

let cache: {
  token: string;
  request: Promise<string | null>;
  isSettled: boolean;
} | null = null;

const requestSessionCustomerId = (token: string) => {
  const entry = {
    token,
    isSettled: false,
    request: fetchSessionCustomerId().catch(() => {
      if (cache === entry) cache = null;
      return null;
    }),
  };
  void entry.request.finally(() => {
    entry.isSettled = true;
  });
  cache = entry;
  return entry.request;
};

/**
 * The signed-in session's own customer id, read once per session token and
 * written over the stored customerId, so a cart never names the customer from
 * a stale stored id (SQR-15987). Null for a guest.
 */
export const getSessionCustomerId = async (): Promise<string | null> => {
  const token = storage.getString(storageKeys.authToken);
  if (!token) return null;

  const customerId = await (cache?.token === token
    ? cache.request
    : requestSessionCustomerId(token));
  // An empty stored id means signup hasn't finished; its last step stores it.
  const storedCustomerId = storage.getString(storageKeys.customerId);
  if (customerId && storedCustomerId && storedCustomerId !== customerId) {
    storage.set(storageKeys.customerId, customerId);
  }
  return customerId;
};

/** Reads the session's customer id again, e.g. after a 403. */
export const syncCustomerIdFromSession = async (): Promise<void> => {
  if (cache?.isSettled) cache = null;
  await getSessionCustomerId();
};
